import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw,
  PlayCircle,
  Clock,
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Users,
  Crown,
  Trash2,
} from "lucide-react";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  trialing: "bg-sky-100 text-sky-700 border-sky-200",
  expired: "bg-red-100 text-red-700 border-red-200",
  past_due: "bg-red-100 text-red-700 border-red-200",
  canceled: "bg-amber-100 text-amber-700 border-amber-200",
  free: "bg-slate-100 text-slate-700 border-slate-200",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BillingLifecyclePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  // Lifetime grants
  const [lifetimeList, setLifetimeList] = useState([]);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantPlan, setGrantPlan] = useState("club");
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: summary }, { data: lifetime }] = await Promise.all([
        api.get("/admin/billing/lifecycle"),
        api.get("/admin/lifetime/list"),
      ]);
      setData(summary);
      setLifetimeList(lifetime || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: summary } = await api.post("/admin/billing/lifecycle/run");
      toast.success(
        `Lifecycle eseguito: ${summary.reminders_sent} reminder, ${summary.expired} scaduti`
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore esecuzione");
    } finally {
      setRunning(false);
    }
  };

  const grantLifetime = async () => {
    const email = grantEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Inserisci una email");
      return;
    }
    setGranting(true);
    try {
      const { data: res } = await api.post("/admin/lifetime/grant", {
        email,
        plan: grantPlan,
      });
      toast.success(res.message);
      setGrantEmail("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Concessione fallita");
    } finally {
      setGranting(false);
    }
  };

  const revokeLifetime = async (email, plan) => {
    if (!window.confirm(`Revocare accesso illimitato a ${email}?`)) return;
    try {
      await api.post("/admin/lifetime/revoke", { email, plan: plan || "club" });
      toast.success(`Revocato a ${email}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoca fallita");
    }
  };

  if (!data && loading) {
    return <div className="text-slate-400 text-center py-10">Caricamento…</div>;
  }
  if (!data) return null;

  const byStatus = data.by_status || {};
  const expiring = data.expiring_7d || [];
  const expired = data.expired_recent || [];

  return (
    <div className="space-y-6" data-testid="billing-lifecycle-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <CreditCard size={22} className="text-sky-600" />
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              Lifecycle Abbonamenti
            </h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Reminder automatici, scadenze e stato globale degli abbonamenti
            (admin only).
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="lifecycle-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
          <Button
            onClick={runNow}
            disabled={running}
            data-testid="lifecycle-run-btn"
          >
            <PlayCircle size={14} className="mr-2" />
            {running ? "Esecuzione…" : "Esegui ora"}
          </Button>
        </div>
      </div>

      {/* Stripe health */}
      <StripeHealthCard />

      {/* Status grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["active", "trialing", "canceled", "expired", "free"].map((s) => (
          <Card key={s} className="p-4">
            <div className="label-eyebrow">{s}</div>
            <div className="text-3xl font-bold mt-1">{byStatus[s] || 0}</div>
            <Badge
              variant="outline"
              className={`mt-1 ${STATUS_COLORS[s]} font-medium`}
            >
              {s}
            </Badge>
          </Card>
        ))}
      </div>

      {/* Lifetime grants */}
      <Card className="p-4 border-amber-200 bg-gradient-to-br from-amber-50/40 to-white">
        <div className="flex items-center gap-2 mb-3">
          <Crown size={18} className="text-amber-500" />
          <h2 className="font-heading font-bold text-lg">
            Accessi illimitati ({lifetimeList.length})
          </h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Concede a un utente accesso permanente al piano scelto, senza
          rinnovi. Bypassa scadenze e feature gating per quel team.
        </p>
        <div className="grid md:grid-cols-12 gap-2 items-end mb-4">
          <div className="md:col-span-7">
            <label className="label-eyebrow block mb-1">Email utente</label>
            <Input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="nico.ferrandina@gmail.com"
              data-testid="lifetime-grant-email"
            />
          </div>
          <div className="md:col-span-3">
            <label className="label-eyebrow block mb-1">Piano</label>
            <Select value={grantPlan} onValueChange={setGrantPlan}>
              <SelectTrigger data-testid="lifetime-grant-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="club">Club (€49,90/mo · €479/yr)</SelectItem>
                <SelectItem value="pro">Pro (€19,90/mo · €191/yr)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Button
              onClick={grantLifetime}
              disabled={granting || !grantEmail.trim()}
              className="w-full"
              data-testid="lifetime-grant-btn"
            >
              <Crown size={14} className="mr-1" />
              {granting ? "..." : "Concedi"}
            </Button>
          </div>
        </div>
        {lifetimeList.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-amber-100/40 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Nome</th>
                  <th className="px-3 py-2 text-center">Piano</th>
                  <th className="px-3 py-2 text-left">Concesso il</th>
                  <th className="px-3 py-2 text-left">Da</th>
                  <th className="px-3 py-2 text-center">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {lifetimeList.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-amber-50/50"
                    data-testid={`lifetime-row-${u.id}`}
                  >
                    <td className="px-3 py-2 font-medium">{u.email}</td>
                    <td className="px-3 py-2 text-slate-600">{u.name || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge
                        variant="outline"
                        className="bg-amber-100 text-amber-800 border-amber-300 font-bold uppercase"
                      >
                        <Crown size={10} className="mr-1" />
                        {u.lifetime_plan}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {u.lifetime_granted_at
                        ? formatDate(u.lifetime_granted_at)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {u.lifetime_granted_by || "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revokeLifetime(u.email, u.lifetime_plan)}
                        data-testid={`lifetime-revoke-${u.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-3">
            Nessun accesso illimitato attivo.
          </p>
        )}
      </Card>

      {/* Expiring soon */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={18} className="text-amber-500" />
          <h2 className="font-heading font-bold text-lg">
            In scadenza nei prossimi 7 giorni ({expiring.length})
          </h2>
        </div>
        {expiring.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">
            Nessun abbonamento in scadenza imminente.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Team</th>
                  <th className="px-3 py-2 text-left">Piano</th>
                  <th className="px-3 py-2 text-left">Scadenza</th>
                  <th className="px-3 py-2 text-center">Giorni</th>
                  <th className="px-3 py-2 text-left">Reminder inviati</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expiring.map((s) => (
                  <tr key={s.team_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-[11px] truncate max-w-[200px]">
                      {s.team_id}
                    </td>
                    <td className="px-3 py-2 capitalize">{s.plan}</td>
                    <td className="px-3 py-2">{formatDate(s.current_period_end)}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge
                        variant="outline"
                        className={
                          s.days_left <= 1
                            ? "bg-red-50 text-red-700 border-red-200"
                            : s.days_left <= 3
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-sky-50 text-sky-700 border-sky-200"
                        }
                      >
                        {s.days_left}d
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {(s.reminders_sent || []).length === 0
                        ? "—"
                        : (s.reminders_sent || [])
                            .sort((a, b) => b - a)
                            .map((t) => `${t}d`)
                            .join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Expired recent */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={18} className="text-red-500" />
          <h2 className="font-heading font-bold text-lg">
            Scaduti recenti — ultimi 30 giorni ({expired.length})
          </h2>
        </div>
        {expired.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">
            Nessun abbonamento scaduto recentemente.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Team</th>
                  <th className="px-3 py-2 text-left">Piano</th>
                  <th className="px-3 py-2 text-left">Scaduto il</th>
                  <th className="px-3 py-2 text-left">Period end</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expired.map((s) => (
                  <tr key={s.team_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-[11px] truncate max-w-[200px]">
                      {s.team_id}
                    </td>
                    <td className="px-3 py-2 capitalize">{s.plan}</td>
                    <td className="px-3 py-2">{formatDate(s.expired_at)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {formatDate(s.current_period_end)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="flex items-center gap-2 text-xs text-slate-400">
        <CheckCircle2 size={12} />
        <span>
          La cron interna gira ogni ora e invia reminder a 7g, 3g, 1g dalla
          scadenza; segna come <code>expired</code> oltre la scadenza.
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Users size={12} />
        <span>
          Totale abbonamenti in DB: <strong>{data.total}</strong>
        </span>
      </div>
    </div>
  );
}

function StripeHealthCard() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stripe/health");
      setHealth(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento Stripe health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!health) {
    return (
      <Card className="p-4 text-sm text-slate-500" data-testid="stripe-health-loading">
        Caricamento stato Stripe…
      </Card>
    );
  }

  const cfg = health.config || {};
  const isLive = cfg.secret_key_mode === "live";
  const modeBadgeClass = isLive
    ? "bg-emerald-100 text-emerald-700 border-emerald-300"
    : cfg.secret_key_mode === "test"
    ? "bg-amber-100 text-amber-700 border-amber-300"
    : "bg-red-100 text-red-700 border-red-300";

  return (
    <Card className="p-5 border-sky-200 bg-gradient-to-br from-sky-50/40 to-white" data-testid="stripe-health-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-lg">Stripe Health Check</h3>
            <Badge
              variant="outline"
              className={`${modeBadgeClass} font-semibold uppercase text-[10px] tracking-widest`}
              data-testid="stripe-health-mode"
            >
              {cfg.secret_key_mode}
            </Badge>
            {!cfg.modes_match && (
              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[10px]">
                modes mismatch
              </Badge>
            )}
            {!cfg.webhook_secret_configured && (
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                webhook secret missing
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Verifica configurazione chiavi, prodotti, webhook e abbonamenti attivi.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="stripe-health-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Config */}
        <div className="p-3 rounded-lg bg-white border border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Configurazione
          </div>
          <ConfigRow label="Secret key" value={cfg.secret_key_mode} ok={cfg.secret_key_mode !== "missing"} />
          <ConfigRow label="Publishable" value={cfg.publishable_key_mode} ok={cfg.publishable_key_mode !== "missing"} />
          <ConfigRow label="Webhook secret" value={cfg.webhook_secret_configured ? "configurato" : "MANCANTE"} ok={cfg.webhook_secret_configured} />
          <ConfigRow label="Frontend URL" value={cfg.frontend_url || "—"} ok={!!cfg.frontend_url} mono />
        </div>

        {/* Products */}
        <div className="p-3 rounded-lg bg-white border border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Prodotti Stripe
          </div>
          {(health.stripe_products || []).map((p) => {
            const ok = p.active && p.amount_match;
            return (
              <div key={p.internal_id} className="flex items-start gap-2 text-xs mb-1.5">
                {ok ? (
                  <CheckCircle2 size={13} className="text-emerald-600 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle size={13} className="text-red-500 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {p.internal_id} <span className="text-slate-400">· €{p.expected_amount_eur}</span>
                  </div>
                  <div className="text-slate-500 truncate font-mono text-[10px]">
                    {p.price_id || "—"}
                  </div>
                  {p.error && <div className="text-red-600 text-[10px] mt-0.5">{p.error}</div>}
                </div>
              </div>
            );
          })}
          {health.stripe_lookup_error && (
            <div className="text-xs text-red-600 mt-1">{health.stripe_lookup_error}</div>
          )}
        </div>

        {/* Activity */}
        <div className="p-3 rounded-lg bg-white border border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Attività
          </div>
          <div className="text-xs">
            <div className="text-slate-500">Ultimo webhook ricevuto:</div>
            {health.last_webhook ? (
              <div className="mt-1">
                <div className="font-medium">{health.last_webhook.type}</div>
                <div className="text-slate-500 text-[11px]">
                  {formatDateTime(health.last_webhook.received_at)}
                  {health.last_webhook.livemode && (
                    <span className="ml-1 text-emerald-700 font-semibold">LIVE</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-amber-700 mt-1">Nessun evento ancora ricevuto</div>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-slate-500 text-xs">Abbonamenti totali:</div>
            <div className="font-heading font-bold text-2xl">
              {health.subscriptions?.total || 0}
            </div>
            {Object.entries(health.subscriptions?.by_status || {}).length > 0 && (
              <div className="text-[10px] text-slate-500 mt-1">
                {Object.entries(health.subscriptions.by_status)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ")}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 text-[10px] text-slate-400 text-right">
        Ultimo check: {formatDateTime(health.checked_at)}
      </div>
    </Card>
  );
}

function ConfigRow({ label, value, ok, mono }) {
  return (
    <div className="flex items-start gap-2 text-xs mb-1">
      {ok ? (
        <CheckCircle2 size={13} className="text-emerald-600 mt-0.5 shrink-0" />
      ) : (
        <AlertTriangle size={13} className="text-red-500 mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <span className="text-slate-500">{label}: </span>
        <span className={`${mono ? "font-mono text-[10px]" : "font-medium"} break-all`}>{value}</span>
      </div>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

